import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Badge, Button, Form, Alert, Modal } from 'react-bootstrap';
import { GeoAlt } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { gpsCheckInApprovalAPI } from '../../services';

/**
 * #210203 — Yêu cầu duyệt check-in do lỗi GPS.
 *
 * Bối cảnh: nhân viên đứng ngay tại quán nhưng GPS điện thoại trôi, hệ thống báo "cách 1307m" và
 * chặn cứng. Luật GPS KHÔNG đổi — bảng này là chỗ quản lý xem bằng chứng rồi quyết định mở khoá
 * cho từng trường hợp.
 *
 * Đặt ngay trong màn Điểm danh (không đẻ màn hình mới): người xử lý đang ở đúng ngữ cảnh chấm
 * công, thấy luôn ca nào bị treo.
 */

const STATUS_BADGE = {
  PENDING: { bg: 'warning', text: 'Chờ duyệt' },
  APPROVED: { bg: 'success', text: 'Đã duyệt' },
  REJECTED: { bg: 'danger', text: 'Đã từ chối' },
  EXPIRED: { bg: 'secondary', text: 'Hết hạn' },
};

const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('vi-VN') : 'N/A');

export default function GpsCheckInApprovalPanel({
  branchId,
  status,
  onStatusChange,
  highlightRequestId,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [acting, setActing] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await gpsCheckInApprovalAPI.list({
        status,
        ...(branchId ? { branch_id: branchId } : {}),
        limit: 100,
      });
      setItems(res?.items || res?.data?.items || []);
    } catch (err) {
      // Nhân viên thường mở trang này sẽ nhận 403 từ server — hiện đúng thông báo đó thay vì
      // bảng rỗng, để không ai tưởng "chi nhánh không có yêu cầu nào".
      setError(
        err?.response?.data?.error?.message ||
          'Không tải được danh sách yêu cầu duyệt check-in lỗi GPS',
      );
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [branchId, status]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async (row) => {
    if (
      !window.confirm(
        `Duyệt check-in cho ${row.employee_name}?\n\nGPS máy nhân viên báo lệch ${row.distance_m}m (cho phép ${row.allowed_radius_m}m).\nCa sẽ được tính check-in lúc ${fmtDateTime(row.requested_at)} — đúng lúc nhân viên bấm gửi.`,
      )
    ) {
      return;
    }
    setActing(row.id);
    try {
      await gpsCheckInApprovalAPI.approve(row.id);
      toast.success('Đã duyệt — chấm công được tạo với giờ vào lúc nhân viên bấm gửi');
      load();
    } catch (err) {
      toast.error(
        err?.response?.data?.error?.message || 'Không duyệt được yêu cầu này',
      );
    } finally {
      setActing(null);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setActing(rejectTarget.id);
    try {
      await gpsCheckInApprovalAPI.reject(rejectTarget.id, rejectReason.trim());
      toast.success('Đã từ chối — không tạo chấm công, nhân viên sẽ thấy lý do');
      setRejectTarget(null);
      setRejectReason('');
      load();
    } catch (err) {
      toast.error(
        err?.response?.data?.error?.message || 'Không từ chối được yêu cầu này',
      );
    } finally {
      setActing(null);
    }
  };

  return (
    <>
      <Card className="mc-card mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h5 className="mb-0">
            <GeoAlt className="me-2" />
            Yêu cầu duyệt check-in do lỗi GPS
          </h5>
          <div className="d-flex align-items-center gap-2">
            <Form.Select
              size="sm"
              style={{ width: 160 }}
              value={status}
              onChange={(e) => onStatusChange(e.target.value)}
            >
              <option value="PENDING">Chờ duyệt</option>
              <option value="APPROVED">Đã duyệt</option>
              <option value="REJECTED">Đã từ chối</option>
              <option value="ALL">Tất cả</option>
            </Form.Select>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={load}
              disabled={loading}
            >
              Tải lại
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <div className="text-center py-3">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : items.length === 0 ? (
            <p className="text-muted mb-0">Không có yêu cầu nào.</p>
          ) : (
            <Table responsive hover size="sm">
              <thead>
                <tr>
                  <th>Nhân viên</th>
                  <th>Chi nhánh</th>
                  <th>Bấm gửi lúc</th>
                  <th>GPS lệch</th>
                  <th>Bằng chứng</th>
                  <th>Lý do nhân viên</th>
                  <th>Trạng thái</th>
                  <th>Xử lý</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const badge = STATUS_BADGE[row.status] || {
                    bg: 'secondary',
                    text: row.status,
                  };
                  const mapUrl = `https://www.google.com/maps?q=${row.gps?.lat},${row.gps?.lng}`;
                  return (
                    <tr
                      key={row.id}
                      className={
                        highlightRequestId === row.id ? 'table-warning' : undefined
                      }
                    >
                      <td>
                        <div>{row.employee_name}</div>
                        <small className="text-muted">
                          Đã xin {row.used_today}/{row.daily_limit} lượt hôm nay
                        </small>
                      </td>
                      <td>{row.branch_name}</td>
                      <td>
                        <div>{fmtDateTime(row.requested_at)}</div>
                        <small className="text-muted">
                          Ca: {fmtDateTime(row.shift_start_time)}
                        </small>
                      </td>
                      <td>
                        <strong className="text-danger">{row.distance_m}m</strong>
                        <div>
                          <small className="text-muted">
                            cho phép {row.allowed_radius_m}m
                          </small>
                        </div>
                      </td>
                      <td style={{ maxWidth: 240 }}>
                        <div>
                          <a href={mapUrl} target="_blank" rel="noreferrer">
                            {row.gps?.lat?.toFixed(6)}, {row.gps?.lng?.toFixed(6)}
                          </a>
                        </div>
                        {row.gps_accuracy_m != null && (
                          <div>
                            <small className="text-muted">
                              Độ chính xác máy báo: ±{Math.round(row.gps_accuracy_m)}m
                            </small>
                          </div>
                        )}
                        {row.device_id && (
                          <div>
                            <small className="text-muted">
                              Thiết bị: {row.device_id.slice(0, 12)}…
                            </small>
                          </div>
                        )}
                        {row.ip_address && (
                          <div>
                            <small className="text-muted">IP: {row.ip_address}</small>
                          </div>
                        )}
                      </td>
                      <td style={{ maxWidth: 240 }}>
                        <div>{row.reason}</div>
                        {row.reject_reason && (
                          <small className="text-danger">
                            Từ chối: {row.reject_reason}
                          </small>
                        )}
                      </td>
                      <td>
                        <Badge bg={badge.bg}>{badge.text}</Badge>
                        {row.reviewer_name && (
                          <div>
                            <small className="text-muted">
                              {row.reviewer_name} — {fmtDateTime(row.reviewed_at)}
                            </small>
                          </div>
                        )}
                      </td>
                      <td>
                        {row.status === 'PENDING' ? (
                          <div className="d-flex gap-1">
                            <Button
                              size="sm"
                              variant="success"
                              disabled={acting === row.id}
                              onClick={() => handleApprove(row)}
                            >
                              Duyệt
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              disabled={acting === row.id}
                              onClick={() => {
                                setRejectTarget(row);
                                setRejectReason('');
                              }}
                            >
                              Từ chối
                            </Button>
                          </div>
                        ) : (
                          <small className="text-muted">—</small>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={!!rejectTarget} onHide={() => setRejectTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Từ chối yêu cầu check-in lỗi GPS</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Nhân viên <strong>{rejectTarget?.employee_name}</strong> sẽ thấy lý do bạn nhập ở đây.
            Không tạo chấm công.
          </p>
          <Form.Group>
            <Form.Label>Lý do từ chối</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="VD: Gọi điện không liên lạc được, chưa xác minh có mặt tại quán"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRejectTarget(null)}>
            Hủy
          </Button>
          <Button
            variant="danger"
            disabled={acting === rejectTarget?.id}
            onClick={handleReject}
          >
            Từ chối
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
